/**
 * ConversationDetail Component
 * 
 * Right panel of the split view showing complete conversation details.
 * Displays customer profile, chat history, and agent assignment interface.
 * 
 * Features:
 * - Customer profile with comprehensive tracking data
 * - Complete chat message timeline
 * - Agent assignment interface
 * - Real-time updates
 * - Loading states and error handling
 * - Responsive layout
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import * as Types from '../../../types'
type ConversationAssignment = Types.ConversationAssignment
type Agent = Types.Agent
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { Select } from '../../ui/Select'
import { Textarea } from '../../ui/Textarea'
import { Badge } from '../../ui/Badge'
import { 
  MessageSquare,
  User,
  Clock,
  Send,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  UserPlus,
  UserCheck,
  Eye,
  Calendar,
  Sparkles,
  XCircle,
  Bot,
  Archive
} from 'lucide-react'
import { conversationHistoryService } from '../../../services/conversationHistory'
import CustomerProfile from './CustomerProfile'
import { ConversationNotesPanel } from './ConversationNotesPanel'
import { logger } from '../../../utils/logger'
import { PrioritySelector, type PriorityLevel } from './PrioritySelector'
import { useUnreadTracking } from '../../../services/unreadTracking'
import { useAuth } from '../../../hooks/useAuth'
import { liveChatService } from '../../../services/liveChat'
import { ModernChatInput } from '../../ui/ModernChatInput'
import { RealTimeChatHistory } from './RealTimeChatHistory'
import { StatusBadge, getStatusConfig } from '../../../utils/statusDisplay'

export interface ConversationDetailProps {
  /** Selected conversation assignment */
  conversation: ConversationAssignment | null
  /** Full conversation data with messages */
  fullConversation?: any
  /** Available agents for assignment */
  agents: Agent[]
  /** Loading state */
  isLoading?: boolean
  /** Callback when conversation is assigned */
  onAssignmentComplete?: (result?: any) => void
  /** Custom CSS classes */
  className?: string
  /** Hide customer profile section (for Professional 3-panel layout) */
  hideCustomerProfile?: boolean
  /** Callback when customer data is updated (for Professional interface) */
  onCustomerDataUpdate?: (conversation: any, profile: any) => void
  /** Callback when priority is changed */
  onPriorityUpdate?: (conversationId: string, newPriority: string, oldPriority: string) => void
}

/**
 * Format time duration for display
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

// Status display functions are now handled by the unified status system
// See /src/utils/statusDisplay.ts for the centralized implementation

/**
 * Assignment form component
 */
function AssignmentForm({ 
  conversation,
  agents,
  onAssignmentComplete
}: {
  conversation: ConversationAssignment
  agents: Agent[]
  onAssignmentComplete?: () => void
}) {
  
  const [selectedAgentId, setSelectedAgentId] = useState('')
  const [handoffReason, setHandoffReason] = useState(conversation.handoff_reason || '')
  const [contextSummary, setContextSummary] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedAgentId || !handoffReason.trim()) {
      setError('Please select an agent and provide a handoff reason')
      return
    }
    
    try {
      setIsSubmitting(true)
      setError(null)
      
      logger.info(`🎯 Assigning conversation ${conversation.conversation_id} to agent ${selectedAgentId}`)
      
      await conversationHistoryService.assignConversation(
        conversation.conversation_id,
        selectedAgentId,
        handoffReason.trim(),
        contextSummary.trim() || undefined
      )
      
      logger.info('✅ Conversation assigned successfully')
      
      // Find the assigned agent details
      const assignedAgent = agents.find(agent => agent.id === selectedAgentId)
      
      // Create assignment result for callback
      const assignmentResult = {
        status: 'assigned', // or 'pending' depending on the flow
        conversationId: conversation.conversation_id,
        agentId: selectedAgentId,
        agentName: assignedAgent?.name,
        reason: handoffReason.trim()
      }
      
      onAssignmentComplete?.(assignmentResult)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign conversation'
      logger.error('❌ Assignment failed:', err)
      setError(errorMessage)
      
    } finally {
      setIsSubmitting(false)
    }
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPlus className="w-4 h-4" />
          Assign to Agent
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Agent Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Agent
            </label>
            <Select
              options={[
                { value: "", label: "Choose an agent..." },
                ...agents.map((agent) => ({
                  value: agent.id,
                  label: `${agent.name} (${agent.role}) - ${agent.current_chat_count}/${agent.max_concurrent_chats} chats`
                }))
              ]}
              value={selectedAgentId}
              onChange={setSelectedAgentId}
              className="w-full"
            />
            
            {selectedAgentId && (
              <div className="mt-2 text-xs text-gray-600">
                {(() => {
                  const agent = agents.find(a => a.id === selectedAgentId)
                  if (!agent) return null
                  
                  const workloadPercentage = Math.round((agent.current_chat_count / agent.max_concurrent_chats) * 100)
                  return (
                    <div className="flex items-center gap-2">
                      <span>Current workload: {workloadPercentage}%</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${workloadPercentage}%` }}
                        />
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
          
          {/* Handoff Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Handoff Reason
            </label>
            <Textarea
              value={handoffReason}
              onChange={(e) => setHandoffReason(e.target.value)}
              placeholder="Why is this conversation being handed off to an agent?"
              rows={3}
              required
              className="w-full"
            />
          </div>
          
          {/* Context Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Context Summary (Optional)
            </label>
            <Textarea
              value={contextSummary}
              onChange={(e) => setContextSummary(e.target.value)}
              placeholder="Provide any relevant context or background information for the agent..."
              rows={3}
              className="w-full"
            />
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Assignment Failed</span>
              </div>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          )}
          
          {/* Submit Button */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!selectedAgentId || !handoffReason.trim() || isSubmitting}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Assign Conversation
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * ConversationDetail component - Right panel of split view
 */
export function ConversationDetail({
  conversation,
  fullConversation,
  agents,
  isLoading,
  onAssignmentComplete,
  className = '',
  hideCustomerProfile = false,
  onCustomerDataUpdate,
  onPriorityUpdate
}: ConversationDetailProps) {
  
  // State for chat interface mode: 'enhanced' only
  const [chatMode, setChatMode] = useState<'enhanced'>('enhanced')
  
  // Chat input state
  const [isNoteMode, setIsNoteMode] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const { user } = useAuth()

  const [currentConversation, setCurrentConversation] = useState(conversation);

  useEffect(() => {
    setCurrentConversation(conversation);
  }, [conversation]);
  
  // Unread tracking service
  const { autoMarkAsReadOnOpen } = useUnreadTracking()
  
  // 🔍 LOG: Track currentConversation prop changes
  useEffect(() => {
    console.log('📋 ConversationDetail: currentConversation prop changed:', {
      conversationId: currentConversation?.conversation_id,
      customerName: currentConversation?.customer_name,
      status: currentConversation?.status,
      timestamp: new Date().toISOString()
    })
    
    // 🐛 DEBUG: Special logging for resolved status to debug close chat issue
    if (currentConversation?.status === 'resolved') {
      console.warn('🐛 DEBUG: ConversationDetail received RESOLVED currentConversation!', {
        conversationId: currentConversation.conversation_id,
        status: currentConversation.status,
        customerName: currentConversation.customer_name,
        assignedAgent: currentConversation.assigned_agent_name,
        fullConversation: currentConversation
      })
    }
  }, [currentConversation])
  
  


  // Auto-mark currentConversation as read when opened - Real-time event-driven
  useEffect(() => {
    if (currentConversation?.conversation_id && !isLoading) {
      // Only mark as read once per currentConversation selection
      const markAsRead = async () => {
        try {
          await autoMarkAsReadOnOpen(currentConversation.conversation_id)
          logger.debug('🔔 Auto-marking currentConversation as read on open', {
            conversationId: currentConversation.conversation_id
          })
        } catch (error) {
          logger.error('Failed to mark currentConversation as read', { error, conversationId: currentConversation.conversation_id })
        }
      }
      
      // Use a flag to prevent multiple calls
      let hasMarked = false
      
      const timer = setTimeout(() => {
        if (!hasMarked) {
          hasMarked = true
          markAsRead()
        }
      }, 1000) // 1 second delay for better UX
      
      return () => {
        clearTimeout(timer)
        hasMarked = true // Prevent execution if component unmounts
      }
    }
  }, [currentConversation?.conversation_id, isLoading, autoMarkAsReadOnOpen])
  
  // Handle assignment completion
  const handleAssignmentComplete = () => {
    logger.info('🔄 Assignment completed, refreshing conversation data')
    onAssignmentComplete?.()
  }
  
  // Show placeholder when no currentConversation selected
  if (!currentConversation) {
    return (
      <div className={`h-full bg-gray-50 ${className}`}>
        <div className="h-full flex flex-col">
          {/* Quick Stats Header */}
          <div className="p-6 border-b border-gray-200 bg-white">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversation Dashboard</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Active Chats</p>
                    <p className="text-2xl font-bold text-blue-900">12</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Avg Response</p>
                    <p className="text-2xl font-bold text-green-900">2.3m</p>
                  </div>
                  <Clock className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 p-6">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                  <MessageSquare className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Conversation</h3>
                <p className="text-gray-600">
                  Choose a conversation from the sidebar to view the full chat history and customer details
                </p>
              </div>
              
              {/* Quick Actions */}
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">View All Conversations</p>
                      <p className="text-sm text-gray-500">Browse through all customer interactions</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-green-300 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <UserPlus className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Agent Assignment</p>
                      <p className="text-sm text-gray-500">Manage agent workload and assignments</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-purple-300 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Bot className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">AI Performance</p>
                      <p className="text-sm text-gray-500">Monitor AI response quality and metrics</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  // 🔧 FIX: Use fullConversation data for header status to ensure real-time updates
  const currentStatus = fullConversation?.status || currentConversation.status
  const currentAssignedAgent = fullConversation?.assigned_agent_name || currentConversation.assigned_agent_name
  
  const statusDisplay = getStatusConfig(currentStatus, currentAssignedAgent)
  
  // 🐛 DEBUG: Log status display calculation for close chat debugging
  useEffect(() => {
    if (currentStatus === 'resolved') {
      console.warn('🐛 DEBUG: StatusDisplay calculation for RESOLVED currentConversation:', {
        conversationId: currentConversation.conversation_id,
        rawStatus: currentStatus,
        statusDisplay: statusDisplay,
        assignedAgentName: currentAssignedAgent,
        fullConversation: fullConversation
      })
    }
  }, [currentStatus, currentAssignedAgent, fullConversation])
  
  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {currentConversation.customer_name || 'Anonymous Customer'}
            </h2>
            <p className="text-sm text-gray-600">
              Conversation ID: {currentConversation.conversation_id ? currentConversation.conversation_id.slice(-8) : 'Unknown'}
            </p>
          </div>
          
          <div className="flex flex-col items-end gap-1">
            <StatusBadge 
              status={currentStatus}
              assignedAgentName={currentAssignedAgent}
            />
            {currentAssignedAgent && (
              <div className="text-xs text-gray-500">
                👨‍💼 {currentAssignedAgent}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">Loading conversation details...</p>
            </div>
          </div>
        )}
        
        
        {/* Main Content - Keep chat mounted to prevent input refresh */}
        {currentConversation && (
          <div className={`
            h-full gap-2 p-2
            ${hideCustomerProfile 
              ? 'flex flex-col' 
              : 'grid grid-rows-[auto_1fr_auto]'
            }
          `}>
            
            {/* Customer Profile (hidden in Professional 3-panel layout) */}
            {!hideCustomerProfile && fullConversation?.customer_profile && (
              <div className="overflow-y-auto max-h-80">
                <CustomerProfile 
                  profile={fullConversation?.customer_profile}
                  showTechnicalDetails={false}
                  expandable={true}
                />
              </div>
            )}
            
            {/* Chat History with Fixed Layout */}
            <div className={`
              ${hideCustomerProfile ? 'flex-1 min-h-0 pb-2' : 'min-h-0 flex-1 pb-2'} flex flex-col
            `}>
              {/* Messages Area - Takes available space but constrained */}
              <Card className="flex-1 mb-2 min-h-0 overflow-hidden flex flex-col">
                <CardHeader className="pb-1 pt-2 flex-shrink-0">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MessageSquare className="w-4 h-4" />
                    Conversation History
                    <Badge variant="outline" className="ml-auto">
                      {fullConversation?.total_messages || 0} messages
                    </Badge>
                    
                    {/* Enhanced Chat Mode Badge */}
                    <div className="flex items-center gap-2 ml-4">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Enhanced Mode
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 p-0 min-h-0">
                  <RealTimeChatHistory
                    conversationId={currentConversation.conversation_id}
                    chatbotId={fullConversation?.chatbot_id || currentConversation.chatbot_id}
                    messages={fullConversation?.messages || []}
                    isLoading={isLoading}
                    className="h-full"
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    isNoteMode={isNoteMode}
                    onToggleNoteMode={setIsNoteMode}
                    onMessageSent={() => {
                      // Trigger refresh of conversation data when message is sent
                      logger.info('💬 Message sent, refreshing conversation data')
                      if (onAssignmentComplete) {
                        onAssignmentComplete()
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </div>
            
            {/* Internal Notes Panel - Phase 6A */}
            {!hideCustomerProfile && (
              <div className="mt-4">
                <ConversationNotesPanel 
                  conversationId={currentConversation.conversation_id}
                  onNotesUpdate={(totalNotes) => {
                    logger.info('Notes panel updated', { 
                      conversationId: currentConversation.conversation_id, 
                      totalNotes 
                    })
                  }}
                  className="max-h-96"
                />
              </div>
            )}
            
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationDetail